import React from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { Box, Paper, styled, Typography } from '@mui/material'

import { CustomTooltip } from '../CustomTooltip/CustomTooltip'

export type UtilisationPieProps = {
  greenPercentage?: number
  bluePercentage?: number
  greenTooltip?: string
  blueTooltip?: string
  redTooltip?: string
  showAsRed?: boolean
  size?: number
}

export const UtilisationPie: React.FC<UtilisationPieProps> = ({
  greenPercentage = 0,
  bluePercentage = 0,
  greenTooltip = 'Weight Utilisation',
  blueTooltip = 'Volume Utilisation',
  redTooltip = 'Alert',
  showAsRed = false,
  size = 24,
}) => {
  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0

    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians)),
    }
  }

  const createArc = (percentage: number, startAngle: number) => {
    const center = size / 2
    const radius = size / 2
    const angle = (percentage * 360) / 100

    const start = polarToCartesian(center, center, radius, startAngle)
    const end = polarToCartesian(center, center, radius, startAngle + angle)
    const largeArcFlag = angle <= 180 ? '0' : '1'

    const d = [
      'M', center, center,
      'L', start.x, start.y,
      'A', radius, radius, 0, largeArcFlag, 1, end.x, end.y,
      'Z',
    ].join(' ')

    return d
  }

  // Colors
  const colors = {
    blue: '#3B82F6',
    green: '#22C55E',
    red: '#EF4444',
  }

  // Normalize percentages to not exceed 100%
  const normalizedGreen = Math.min(Math.max(0, greenPercentage), 100)
  const normalizedBlue = normalizedGreen === 100 ? 0 : Math.min(Math.max(0, bluePercentage), 100)

  const tooltipContent = (
    <Paper sx={{ padding: pxToRem(8) }}>
      {showAsRed ? (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <StyledDot style={{ backgroundColor: colors.red }} />
          <StyledTypography variant="body1">{redTooltip}</StyledTypography>
        </Box>
      ) : (
        <>
          {normalizedBlue > 0 && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <StyledDot style={{ backgroundColor: colors.blue }} />
              <StyledTypography variant="body1">{blueTooltip}</StyledTypography>
            </Box>
          )}
          {normalizedGreen > 0 && normalizedBlue > 0 && <br />}
          {normalizedGreen > 0 && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <StyledDot style={{ backgroundColor: colors.green }} />
              <StyledTypography variant="body1">{greenTooltip}</StyledTypography>
            </Box>
          )}
        </>
      )}
    </Paper>
  )

  return (
    <CustomTooltip
      title={tooltipContent}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
        >
          {showAsRed ? (
            // Full red circle
            <circle
              cx={size / 2}
              cy={size / 2}
              r={size / 2}
              fill={colors.red}
            />
          ) : (
            <>
              <circle
                cx={size / 2}
                cy={size / 2}
                r={size / 2}
                fill="#E5E7EB"
              />

              {normalizedBlue > 0 && (
                <path
                  d={createArc(normalizedBlue, 0)}
                  fill={colors.blue}
                />
              )}

              {normalizedGreen > 0 && (
                <path
                  d={createArc(normalizedGreen, normalizedBlue * 3.6)}
                  fill={colors.green}
                />
              )}
              {normalizedGreen === 100 && (
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={size / 2}
                  fill={colors.green}
                />
              )}

            </>
          )}
        </svg>
      </Box>
    </CustomTooltip>
  )
}

const StyledDot = styled('div')`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
`

const StyledTypography = styled(Typography)`
  font-sie: ${pxToRem(12)};
`